import React, { useEffect, useState } from 'react'
import { MESSAGE_TYPE, useFloatMessage } from './../../Providers/FloatMessage'
import { SecondaryBase } from './../Commons/SecondaryBase'
import { Confirm } from './../../Commons/Modals/Confirm'

import { ResetCurrentDynamic as BLResetCurrentDynamic } from './../../../utilities/BL/Zones'

// IMPORTACIÓN DE LA CAPA BL DE PICNIC
import { PreparePicnicDayScreen as BLPreparePicnicDayScreen, StartPicnicDayMatch as BLStartPicnicDayMatch, StopPicnicDayMatch as BLStopPicnicDayMatch } from './../../../utilities/BL/Picnic'

/**
 * ==============================================================================
 * DICCIONARIO DE PRESETS DE DIFICULTAD
 * Permite al Admin configurar el juego con un solo clic.
 * ==============================================================================
 */
const GAME_PRESETS = {
    lento:  { roundDurationSeconds: 45, fruitSpawnRateMs: 800, flySpawnRateMs: 2500, baseFallSpeed: 0.3, fruitScore: 1, flyPenalty: 1, flySmashedReward: 2 },
    normal: { roundDurationSeconds: 45, fruitSpawnRateMs: 500, flySpawnRateMs: 1500, baseFallSpeed: 0.6, fruitScore: 1, flyPenalty: 3, flySmashedReward: 2 },
    rapido: { roundDurationSeconds: 45, fruitSpawnRateMs: 300, flySpawnRateMs: 1000, baseFallSpeed: 0.9, fruitScore: 1, flyPenalty: 3, flySmashedReward: 3 },
    experto:{ roundDurationSeconds: 60, fruitSpawnRateMs: 200, flySpawnRateMs: 600,  baseFallSpeed: 1.2, fruitScore: 1, flyPenalty: 5, flySmashedReward: 5 }
};
// const GAME_PRESETS = {
//     lento:  { roundDurationSeconds: 45, fruitSpawnRateMs: 800, flySpawnRateMs: 2500, baseFallSpeed: 0.3, fruitScore: 1, flyPenalty: 1 },
//     normal: { roundDurationSeconds: 45, fruitSpawnRateMs: 500, flySpawnRateMs: 1500, baseFallSpeed: 0.6, fruitScore: 1, flyPenalty: 3 },
//     rapido: { roundDurationSeconds: 45, fruitSpawnRateMs: 300, flySpawnRateMs: 1000, baseFallSpeed: 0.9, fruitScore: 1, flyPenalty: 3 },
//     experto:{ roundDurationSeconds: 60, fruitSpawnRateMs: 200, flySpawnRateMs: 600,  baseFallSpeed: 1.2, fruitScore: 1, flyPenalty: 5 }
// };

/**
 * ==============================================================================
 * COMPONENTE PRINCIPAL DEL ADMIN PARA DÍA DE PICNIC
 * ==============================================================================
 */
const Picnic = ({ getOutOfTheDynamic }) => {
    const { showMessage } = useFloatMessage();

    // --------------------------------------------------------------------------
    // 1. ESTADOS DE DATOS (CONFIGURACIÓN DEL JUEGO)
    // --------------------------------------------------------------------------
    const [gameSettings, setGameSettings] = useState(GAME_PRESETS.normal);

    // --------------------------------------------------------------------------
    // 2. ESTADOS DE CONTROL Y LOGÍSTICA DE LA PARTIDA
    // --------------------------------------------------------------------------
    const [currentSystemStatus, setCurrentSystemStatus] = useState('Juego inactivo. Configura la partida.');
    const [countdownTimerSeconds, setCountdownTimerSeconds] = useState(null);
    const [activeMatchId, setActiveMatchId] = useState(null); // Guarda el ID de la partida en curso
    const [isMatchRunning, setIsMatchRunning] = useState(false);

    // --------------------------------------------------------------------------
    // 3. ESTADOS DEL MODAL DE CONFIRMACIÓN
    // --------------------------------------------------------------------------
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [confirmModalData, setConfirmModalData] = useState(null);

    // --------------------------------------------------------------------------
    // 4. CICLO DE VIDA (Memoria Local y Cronómetro)
    // --------------------------------------------------------------------------
    
    // Al montar el componente, buscamos si hay configuración guardada
    useEffect(() => {
        const savedSettingsString = localStorage.getItem('picnicDayCustomSettings');
        if (savedSettingsString) {
            try {
                setGameSettings(JSON.parse(savedSettingsString));
            } catch (error) {
                console.error("Error leyendo configuración guardada:", error);
            }
        }
    }, []);

    // // Motor del cronómetro de apoyo visual para el Admin
    // useEffect(() => {
    //     let intervalReference = null;
    //     if (countdownTimerSeconds !== null && countdownTimerSeconds > 0) {
    //         intervalReference = setInterval(() => {
    //             setCountdownTimerSeconds(previousSeconds => previousSeconds - 1);
    //         }, 1000);
    //     } else if (countdownTimerSeconds === 0) {
    //         clearInterval(intervalReference);
    //         if (isMatchRunning) {
    //             setCurrentSystemStatus('⏳ El tiempo terminó. Por favor presiona "Terminar y Mostrar Ganador".');
    //         }
    //     }
    //     return () => clearInterval(intervalReference);
    // }, [countdownTimerSeconds, isMatchRunning]);

    // Motor del cronómetro de apoyo visual para el Admin
    useEffect(() => {
        let intervalReference = null;
        
        if (countdownTimerSeconds !== null && countdownTimerSeconds > 0) {
            // Cuenta regresiva normal
            intervalReference = setInterval(() => {
                setCountdownTimerSeconds(previousSeconds => previousSeconds - 1);
            }, 1000);
        } else if (countdownTimerSeconds === 0 && isMatchRunning) {
            // ¡LLEGÓ A CERO! Limpiamos el intervalo y disparamos el final automáticamente
            clearInterval(intervalReference);
            
            // Invocamos la orden de detener y mostrar ganador
            executeStopAndShowWinnerSequence();
        }
        
        return () => clearInterval(intervalReference);
        
        // Deshabilitamos la regla de dependencias exhaustivas de eslint para este efecto
        // ya que solo queremos que reaccione al countdownTimerSeconds y no cree bucles.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [countdownTimerSeconds, isMatchRunning]);

    // --------------------------------------------------------------------------
    // 5. FUNCIONES DE CONFIGURACIÓN DE UI
    // --------------------------------------------------------------------------
    const handleUpdateSettingProperty = (propertyName, newValue) => {
        setGameSettings(previousSettings => ({
            ...previousSettings,
            [propertyName]: newValue
        }));
    };

    const handleApplyPreset = (presetKeyName) => {
        const selectedPresetData = GAME_PRESETS[presetKeyName];
        setGameSettings(selectedPresetData);
        showMessage({ type: MESSAGE_TYPE.INFO, text: `Se aplicó el preset: ${presetKeyName.toUpperCase()}` });
    };

    const handleSaveConfigurationToMemory = () => {
        localStorage.setItem('picnicDayCustomSettings', JSON.stringify(gameSettings));
        showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Configuración guardada para futuras partidas.' });
    };

    // --------------------------------------------------------------------------
    // 6. FUNCIONES DE COMUNICACIÓN CON LA API (WRAPPERS)
    // --------------------------------------------------------------------------
    // const executePrepareSequence = async () => {
    //     const response = await BLPreparePicnicDayScreen();
    //     if (response.success) {
    //         setCurrentSystemStatus('Mostrando portada de Selección de Jugador ⏳');
    //         showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Portada enviada exitosamente a la pantalla.' });
    //         return { success: true };
    //     }
    //     return { success: false, message: response.message };
    // };
    const executePrepareSequence = async () => {
        const response = await BLPreparePicnicDayScreen();
        if (response.success) {
            // NUEVO: Al preparar las pantallas, reseteamos la memoria local de la partida.
            // Esto vuelve a habilitar el botón de "Paso 2: Iniciar Juego" al instante.
            setIsMatchRunning(false);
            setActiveMatchId(null);
            setCountdownTimerSeconds(null);

            setCurrentSystemStatus('Mostrando portada de Selección de Jugador ⏳');
            showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Partida reseteada. Pantallas en Espera.' });
            return { success: true };
        }
        return { success: false, message: response.message };
    };

    const executeStartSequence = async () => {
        const response = await BLStartPicnicDayMatch(gameSettings);
        if (response.success) {
            setActiveMatchId(response.data.matchId);
            setIsMatchRunning(true);
            setCountdownTimerSeconds(gameSettings.roundDurationSeconds);
            setCurrentSystemStatus(`¡JUEGO EN CURSO! 🟢 (${gameSettings.roundDurationSeconds}s)`);
            showMessage({ type: MESSAGE_TYPE.SUCCESS, text: '¡Partida iniciada en los celulares!' });
            return { success: true };
        }
        return { success: false, message: response.message };
    };

    const executeStopAndShowWinnerSequence = async () => {
        const response = await BLStopPicnicDayMatch(activeMatchId);
        if (response.success) {
            setIsMatchRunning(false);
            setActiveMatchId(null);
            setCountdownTimerSeconds(null);
            
            // Evaluamos ganador para el Toast
            let winnerMessage = "¡Hubo un Empate!";
            if (response.data.winner === 'lucho') winnerMessage = "¡Ganó LUCHO!";
            if (response.data.winner === 'tunita') winnerMessage = "¡Ganó TUNITA!";

            setCurrentSystemStatus(`Juego Terminado 🛑 - ${winnerMessage}`);
            showMessage({ type: MESSAGE_TYPE.SUCCESS, text: `Resultados guardados y enviados. ${winnerMessage}` });
            return { success: true };
        }
        return { success: false, message: response.message };
    };

    // --------------------------------------------------------------------------
    // 7. UTILITARIOS VISUALES Y NAVEGACIÓN
    // --------------------------------------------------------------------------
    const openConfirmationModal = (messageToDisplay, actionFunctionToExecute, yesButtonText) => {
        setConfirmModalData({
            message: messageToDisplay, 
            actionToYesButton: actionFunctionToExecute, 
            textYesButton: yesButtonText, 
            textNoButton: 'Cancelar'
        });
        setShowConfirmModal(true);
    };

    const returnToMainDashboard = async () => {
        try {
            await BLResetCurrentDynamic();
            if (getOutOfTheDynamic) getOutOfTheDynamic();
        } catch (error) {
            console.error("Error al regresar:", error);
        }
    };

    // ==========================================================================
    // RENDERIZADO DEL PANEL
    // ==========================================================================
    return (
        <SecondaryBase actionToExecute={returnToMainDashboard}>
            
            {/* --- CABECERA Y ESTATUS --- */}
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-4">
                <p className='AdmRunToGoal__Title mb-3 mb-md-0'>
                    <i className="bi bi-basket3-fill text-warning me-2"></i> Día de Picnic (Atrapa Moscas)
                </p>
                <div className="d-flex align-items-center">
                    {countdownTimerSeconds !== null && (
                        <div className={`badge ${countdownTimerSeconds === 0 ? 'bg-danger' : 'bg-success'} fs-5 me-3 px-3 py-2 shadow-sm`}>
                            {countdownTimerSeconds > 0 ? `⏱️ Tiempo: ${countdownTimerSeconds}s` : '⚠️ TIEMPO AGOTADO'}
                        </div>
                    )}
                    <div className="alert alert-info py-2 px-3 mb-0 font-weight-bold shadow-sm" style={{ fontSize: '0.9rem' }}>
                        <i className="bi bi-broadcast me-2"></i> Status: {currentSystemStatus}
                    </div>
                </div>
            </div>

            <div className="row">
                {/* --- BLOQUE 1: CONFIGURACIÓN DE FÍSICAS --- */}
                <div className="col-md-7 mb-4">
                    <div className="card shadow-sm h-100 border-dark">
                        <div className="card-header bg-dark text-white font-weight-bold d-flex justify-content-between align-items-center">
                            <span><i className="bi bi-sliders me-2"></i> 1. Ajustes del Motor de Juego</span>
                            <button className="btn btn-sm btn-primary" onClick={handleSaveConfigurationToMemory} title="Guardar configuración en navegador">
                                <i className="bi bi-floppy-fill"></i> Guardar
                            </button>
                        </div>
                        
                        <div className="card-body">
                            {/* Botones de Presets Rápido */}
                            <div className="mb-4 text-center border-bottom pb-3">
                                <p className="small text-muted font-weight-bold mb-2">PRESETS RÁPIDOS:</p>
                                <div className="btn-group" role="group">
                                    <button className="btn btn-outline-secondary btn-sm" onClick={() => handleApplyPreset('lento')}>🐢 Lento</button>
                                    <button className="btn btn-outline-success btn-sm" onClick={() => handleApplyPreset('normal')}>🚶 Normal</button>
                                    <button className="btn btn-outline-warning btn-sm" onClick={() => handleApplyPreset('rapido')}>🏃 Rápido</button>
                                    <button className="btn btn-outline-danger btn-sm" onClick={() => handleApplyPreset('experto')}>🔥 Experto</button>
                                </div>
                            </div>

                            <div className="row">
                                <div className="col-md-6 mb-3">
                                    <label className="font-weight-bold small text-primary"><i className="bi bi-stopwatch"></i> Duración Partida (segs)</label>
                                    <input type="number" className="form-control" value={gameSettings.roundDurationSeconds} min="10" onChange={(e) => handleUpdateSettingProperty('roundDurationSeconds', parseInt(e.target.value) || 0)} disabled={isMatchRunning} />
                                </div>
                                <div className="col-md-6 mb-3">
                                    <label className="font-weight-bold small text-info"><i className="bi bi-speedometer2"></i> Velocidad Caída (Base)</label>
                                    <input type="number" className="form-control" value={gameSettings.baseFallSpeed} step="0.1" min="0.1" onChange={(e) => handleUpdateSettingProperty('baseFallSpeed', parseFloat(e.target.value) || 0)} disabled={isMatchRunning} />
                                    <small className="text-muted" style={{fontSize: '0.7rem'}}>Recomendado: 0.3 a 1.2</small>
                                </div>

                                <div className="col-md-6 mb-3">
                                    <label className="font-weight-bold small text-success"><i className="bi bi-apple"></i> Rate Frutas (ms)</label>
                                    <input type="number" className="form-control" value={gameSettings.fruitSpawnRateMs} min="100" onChange={(e) => handleUpdateSettingProperty('fruitSpawnRateMs', parseInt(e.target.value) || 0)} disabled={isMatchRunning} />
                                    <small className="text-muted" style={{fontSize: '0.7rem'}}>Menor = Salen más rápido</small>
                                </div>
                                <div className="col-md-6 mb-3">
                                    <label className="font-weight-bold small text-danger"><i className="bi bi-bug-fill"></i> Rate Moscas (ms)</label>
                                    <input type="number" className="form-control" value={gameSettings.flySpawnRateMs} min="100" onChange={(e) => handleUpdateSettingProperty('flySpawnRateMs', parseInt(e.target.value) || 0)} disabled={isMatchRunning} />
                                </div>

                                <div className="col-md-6 mb-2">
                                    <label className="font-weight-bold small text-success"><i className="bi bi-plus-circle"></i> Puntos x Fruta atrapada</label>
                                    <input type="number" className="form-control" value={gameSettings.fruitScore} min="1" onChange={(e) => handleUpdateSettingProperty('fruitScore', parseInt(e.target.value) || 0)} disabled={isMatchRunning} />
                                </div>
                                <div className="col-md-4 mb-2">
                                    <label className="font-weight-bold small text-warning" title="Puntos al hacer tap en la mosca"><i className="bi bi-bullseye"></i> Aplastar Mosca</label>
                                    <input type="number" className="form-control" value={gameSettings.flySmashedReward} min="1" onChange={(e) => handleUpdateSettingProperty('flySmashedReward', parseInt(e.target.value) || 0)} disabled={isMatchRunning} />
                                </div>
                                <div className="col-md-6 mb-2">
                                    <label className="font-weight-bold small text-danger"><i className="bi bi-dash-circle"></i> Penalización x Mosca que cae</label>
                                    <input type="number" className="form-control" value={gameSettings.flyPenalty} min="1" onChange={(e) => handleUpdateSettingProperty('flyPenalty', parseInt(e.target.value) || 0)} disabled={isMatchRunning} />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* --- BLOQUE 2: BOTONERA DE CONTROL EN VIVO --- */}
                <div className="col-md-5 mb-4">
                    <div className="card shadow-sm h-100 border-success">
                        <div className="card-header bg-success text-white font-weight-bold">
                            <i className="bi bi-joystick me-2"></i> 2. Transmisión a Dispositivos
                        </div>
                        <div className="card-body d-flex flex-column">
                            
                            <div className="alert bg-light border-success pb-3 pt-3 mb-4 shadow-sm text-center">
                                <h6 className="font-weight-bold text-success mb-2">PASOS A SEGUIR</h6>
                                <p className="mb-0 small text-muted">1. Prepara a los jugadores mostrando la portada.<br/>2. Inicia la partida.<br/>3. Al terminar el tiempo, muestra el ganador.</p>
                            </div>

                            <button className="btn btn-outline-secondary mb-3 btn-lg font-weight-bold shadow-sm" 
                                onClick={() => openConfirmationModal('¿Mostrar la pantalla de "Selecciona tu Personaje" (Lucho/Tunita) en celulares y pantalla gigante?', executePrepareSequence, 'Paso 1: Preparar')}>
                                <i className="bi bi-display me-2"></i> Paso 1: Preparar Pantallas
                            </button>
                            
                            <button className="btn btn-success mb-4 btn-lg font-weight-bold shadow" style={{ fontSize: '1.2rem' }} disabled={isMatchRunning}
                                onClick={() => openConfirmationModal('¿Arrancar la partida ahora mismo? Empezarán a caer frutas y moscas.', executeStartSequence, '¡Paso 2: INICIAR JUEGO!')}>
                                <i className="bi bi-play-circle-fill me-2"></i> Paso 2: INICIAR JUEGO
                            </button>
                            
                            <hr className="w-100 mt-auto" />

                            <button className={`btn font-weight-bold w-100 py-3 ${isMatchRunning ? 'btn-danger shadow-lg' : 'btn-outline-danger'}`} 
                                onClick={() => openConfirmationModal('¿Detener la partida, calcular al ganador y mostrar la pantalla final de resultados?', executeStopAndShowWinnerSequence, 'Paso 3: Terminar y Mostrar Ganador')}>
                                <i className="bi bi-trophy-fill me-1"></i> Paso 3: Terminar y Mostrar Ganador
                            </button>
                                    
                            {/* NUEVO BOTÓN: ABORTAR / REINICIAR */}
                            <button className="btn btn-outline-danger mb-3 btn-sm font-weight-bold w-100" 
                                onClick={() => openConfirmationModal('¿Estás seguro de abortar la partida actual? Los puntos no se guardarán.', executePrepareSequence, 'Sí, abortar y reiniciar')}>
                                <i className="bi bi-arrow-counterclockwise me-1"></i> Abortar Partida y Reiniciar
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* INTEGRACIÓN DEL MODAL ESTANDARIZADO DE FDM */}
            {showConfirmModal && (
                <Confirm 
                    message={confirmModalData?.message}
                    actionToYesButton={confirmModalData?.actionToYesButton}
                    textYesButton={confirmModalData?.textYesButton}
                    textNoButton={confirmModalData?.textNoButton}
                    setShowModal={setShowConfirmModal}
                    setConfirmData={setConfirmModalData} 
                />
            )}
            
        </SecondaryBase>
    );
};

export { Picnic };